import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * The app is light-only. Dark mode was removed entirely — there are no `dark:`
 * utilities left and Tailwind's darkMode strategy is off.
 *
 * 'retro' is a whole-app visual skin: an 8-bit treatment applied by a single
 * class on <html>, restyled from retro.css. It changes presentation only, never
 * layout structure or behaviour.
 */
export type Theme = 'default' | 'retro';

const STORAGE_KEY = 'litrlly_theme';
const RETRO_CLASS = 'retro';

interface ThemeContextType {
  theme: Theme;
  isRetro: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const readStoredTheme = (): Theme => {
  try {
    return localStorage.getItem(STORAGE_KEY) === RETRO_CLASS ? 'retro' : 'default';
  } catch {
    // Storage can be unavailable in private browsing; fall back to default.
    return 'default';
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    document.documentElement.classList.toggle(RETRO_CLASS, theme === 'retro');

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // A visitor with storage disabled still gets the theme for this session.
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => setThemeState(next), []);

  const toggleTheme = useCallback(
    () => setThemeState(current => (current === 'retro' ? 'default' : 'retro')),
    []
  );

  return (
    <ThemeContext.Provider value={{ theme, isRetro: theme === 'retro', setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
